import type { ReactNode } from "react";
import type { CompanyProfile } from "@/api/types";
import { formatDate } from "@/lib/dates";
import { amountInWords } from "@/lib/money";
import { usePrintBranding } from "./branding";
import { useTaxWords } from "@/app/session";

/**
 * The parts every printed document shares: who issued it, who it is for, and what it is.
 *
 * Shared because they are the parts a tax authority cares about, and three copies of a seller block
 * is three places for the tax registration to be wrong on one of them. How it's laid out and coloured
 * is the company's branding.
 */
export function PrintHeader({
  seller,
  scope = "invoice",
  documentTitle,
  number,
  date,
  buyerLabel,
  buyerName,
  buyerTaxId,
  buyerRegistration,
}: {
  seller: CompanyProfile | undefined;
  /** Which of the company's templates this document takes. */
  scope?: "invoice" | "receipt";
  documentTitle: string;
  number: string | null;
  date: string;
  buyerLabel: string;
  buyerName: string;
  buyerTaxId?: string;
  buyerRegistration?: string;
}) {
  const say = useTaxWords();
  const branding = usePrintBranding(scope);
  const centred = branding.template === "centred";
  const compact = branding.template === "compact";
  const contact = [seller?.contactPhone, seller?.contactEmail].filter(Boolean).join(" · ");

  const company = (
    <div className={centred ? "text-center" : "min-w-0"}>
      {branding.showLogo && branding.logoData ? (
        <img
          src={branding.logoData}
          alt=""
          className={`mb-2 ${compact ? "max-h-8" : "max-h-14"} max-w-48 ${centred ? "mx-auto" : ""}`}
        />
      ) : null}
      <p className="text-foreground text-sm font-medium">{seller?.legalName ?? ""}</p>
      <p className="text-muted-foreground text-xs">
        {seller ? `${seller.address.street}, ${seller.address.municipality}` : ""}
      </p>
      {branding.showTaxNumber && seller?.pan ? (
        <p className="text-muted-foreground num text-xs">
          {say("PAN")} {seller.pan}
        </p>
      ) : null}
      {branding.showContact && contact ? (
        <p className="text-muted-foreground text-xs">{contact}</p>
      ) : null}
    </div>
  );

  const title = (
    <div className={centred ? "mt-3 text-center" : "shrink-0 text-right"}>
      <h1 className="text-lg font-semibold" style={{ color: branding.accent }}>
        {documentTitle}
      </h1>
      <p className="text-muted-foreground num mt-1 text-xs">
        {documentTitle} no {number ?? "—"}
      </p>
      <p className="text-muted-foreground num text-xs">Date {formatDate(date)}</p>
    </div>
  );

  return (
    <header className={compact ? "mb-3" : "mb-6"}>
      <div className={centred ? "" : "flex items-start justify-between gap-6"}>
        {company}
        {title}
      </div>
      <div
        className={compact ? "my-2 border-t-2" : "my-4 border-t-2"}
        style={{ borderColor: branding.accent }}
      />
      <p className="text-muted-foreground text-xs">{buyerLabel}</p>
      <p className="text-foreground text-sm font-medium">{buyerName}</p>
      {buyerTaxId ? (
        <p className="text-muted-foreground num text-xs">
          {say("PAN")} {buyerTaxId}
        </p>
      ) : null}
      {buyerRegistration ? (
        <p className="text-muted-foreground num text-xs">Registration {buyerRegistration}</p>
      ) : null}
    </header>
  );
}

/** The amount in words, under the totals. Rupees and paisa, so only for an amount in rupees. */
export function PrintAmountInWords({
  scope = "invoice",
  amount,
  currency,
}: {
  scope?: "invoice" | "receipt";
  amount: string;
  currency?: string;
}) {
  const branding = usePrintBranding(scope);
  if (!branding.showAmountInWords || (currency !== undefined && currency !== "NPR")) return null;
  return (
    <p className="text-muted-foreground mt-3 text-xs">Amount in words: {amountInWords(amount)}</p>
  );
}

/** Two lines to sign on, when the company prints them. */
export function PrintSignature({
  scope = "invoice",
  byLabel,
}: {
  scope?: "invoice" | "receipt";
  byLabel: string;
}) {
  const branding = usePrintBranding(scope);
  if (!branding.showSignature) return null;
  return (
    <div className="mt-12 flex justify-between gap-12 text-xs">
      <p className="border-border text-muted-foreground w-48 border-t pt-1">{byLabel}</p>
      <p className="border-border text-muted-foreground w-48 border-t pt-1">Authorised signatory</p>
    </div>
  );
}

/** The line at the foot of every sheet: when it was printed. */
export function PrintFooter({ note }: { note?: ReactNode }) {
  return (
    <footer className="border-border text-muted-foreground mt-8 border-t pt-3 text-[10px]">
      {note ? <p className="text-muted-foreground mb-1 text-xs">{note}</p> : null}
      <p>Printed on {formatDate(new Date().toISOString().slice(0, 10))}</p>
    </footer>
  );
}
